<template>
  <nav
    id="siteNavigation"
    class="navbar h-24 has-shadow shadow-[1px_2px_2px_rgba(0,0,0,0.1),2px_4px_5px_rgba(0,0,0,0.1)] sticky top-0"
    :class="{ open: isNavActive }"
  >
    <div class="is-paddingless-touch relative container h-full">
      <div class="flex navbar-brand items-stretch h-full">
        <router-link
          class="navbar-item"
          to="/"
          alt="Home"
        >
          <img
            class="image h-12"
            src="https://assets.copytechgroup.co.uk/images/copytech-logo.svg"
            alt="Copytech logo"
          />
        </router-link>

        <basket-toggle
          class="is-hidden-desktop ml-auto"
          :is-active="isDrawerActive"
          @toggle-basket="toggleMenu('drawer')"
        />

        <div
          id="nav-toggle"
          class="navbar-burger is-hidden-desktop cursor-pointer hover:bg-slate-100 h-full aspect-square sm:grid place-items-center"
          :class="{ 'is-active': isNavActive }"
          @click="toggleMenu('nav')"
        >
          <span />
          <span />
          <span />
        </div>
      </div>

      <div
        class="navbar-menu top-18 hidden lg:flex"
        :class="{ 'is-active': isNavActive }"
      >
        <div class="navbar-end">
          <navigation
            :device-nav="false"
            @navigate="navigate('nav')"
          />
        </div>
        <basket-toggle
          class="is-hidden-touch"
          :is-active="isDrawerActive"
          @toggle-basket="toggleMenu('drawer')"
        />
      </div>

      <transition name="fade">
        <div
          v-show="isDrawerActive"
          class="overlay is-hidden-touch"
        />
      </transition>
      <!-- Navigation drawer component -->

      <DrawerComponent
        :is-drawer-open="isNavActive"
        @toggle-drawer="toggleMenu('nav')"
        class="top-full lg:top-0 is-left"
        :no-controls="true"
      >
        <template #default>
          <div class="grow px-6">
            <navigation
              :device-nav="false"
              @navigate="navigate('nav')"
            />
          </div>
        </template>
      </DrawerComponent>

      <!-- Basket drawer component -->
      <DrawerComponent
        :is-drawer-open="isDrawerActive"
        @toggle-drawer="toggleMenu('drawer')"
        class="top-full lg:top-0 is-right"
        :no-controls="true"
      >
        <template #default>
          <div class="grow px-6">
            <component
              :is="quoteComponent"
              @close="toggleMenu('drawer')"
              @submit-form="submitQuoteRequest"
            />
          </div>
        </template>
      </DrawerComponent>

      <!-- <div
        class="navbar-drawer top-full is-desktop is-right border-t border-zinc-200"
        :class="{ 'is-active': isDrawerActive }"
      >
        <div
          class="navbar-basket is-hidden-touch has-dropdown has-items-right"
          title="Close basket"
          @click="toggleMenu('drawer')"
        >
          <div class="basket-toggler h-full">
            <span class="icon is-large">
              <Icon v-once
                name="faXmark"
                variant="light"
              />
            </span>
          </div>
        </div>
        <component
          :is="quoteComponent"
          @close="toggleMenu('drawer')"
        />
      </div> -->
    </div>
  </nav>
</template>

<script setup>
import { ref, shallowRef, computed, watch } from 'vue'
import { logger } from '@repo/helpers/util/logger.js'
import { useModal } from '@/composables/useModal.js'
import { useBasketStore } from '@/store/modules/basket.ts'
import { Icon } from '@repo/ui'
import Navigation from './navigation/Navigation.vue'
import BasketToggle from './BasketToggle.vue'
import Basket from './Basket.vue'
import { QuoteDetails } from '@repo/ui'
import Confirmation from './Confirmation.vue'
// import SlideMenu from './SlideMenu.vue'
import { DrawerComponent } from '@repo/ui'

const emit = defineEmits(['drawer-toggle'])

const componentMap = {
  Basket,
  QuoteDetails,
  Confirmation,
}

const modal = useModal()
const basket = useBasketStore()

const { submitQuote } = basket

const isNavActive = ref(false)
const isDrawerActive = ref(false)

const modalComponent = computed(() => modal.getModalComponent.value)
const quoteComponent = computed(() => {
  const componentName = modalComponent.value
  if (componentName === 'QuoteDetails' || componentName === 'Confirmation') {
    return componentMap[componentName]
  } else {
    return componentMap['Basket']
  }
})
const basketOpen = computed(() => basket.isOpen)

watch(basketOpen, (newVal) => {
  toggleMenu('drawer')
})

function toggleScrolling() {
  const scrollClass = 'u-no-scroll'
  const el = document.documentElement

  if (el.classList.contains(scrollClass)) {
    el.classList.remove(scrollClass)
  } else {
    el.classList.add(scrollClass)
  }
}

function toggleOverlay() {
  emit('drawer-toggle')
}

function escListener(menu, e) {
  const key = e.key

  if (key === 'Escape') {
    toggleMenu(menu)
  }
}

function toggleEscListener(menu) {
  document.addEventListener('keydown', escListener.bind(null, menu), {
    once: true,
  })
}

async function submitQuoteRequest(contactDetails) {
  // submissionStatus.value = 'submitting'
  try {
    await submitQuote(contactDetails)

    // submissionStatus.value = 'success'
    modal.switchModal('Confirmation')
  } catch (error) {
    logger.error('Error submitting quote:', error)
    // TODO: Reset form and show error
    // TODO: Add a timeout to submissions or last submit date so taht we dont get spammed
  }
}

function toggleMenu(menu) {
  if (window.matchMedia('(max-width: 1007px)').matches) {
    // If this is toggling nav inactive, ensure show back is false
    if (!isNavActive.value && !isDrawerActive.value) {
      if (menu === 'nav') {
        isNavActive.value = true
        toggleEscListener(menu)
      } else if (menu === 'drawer') {
        isDrawerActive.value = true
        toggleEscListener(menu)
      }
      // Also remove scrolling class from document
      toggleScrolling()
    } else {
      if (menu === 'nav') {
        isNavActive.value = false
      } else if (menu === 'drawer') {
        isDrawerActive.value = false
        basket.closeBasket()
      }

      // Activating nav
      toggleScrolling()
    }
  } else {
    if (!isNavActive.value && !isDrawerActive.value) {
      if (menu === 'nav') {
      } else if (menu === 'drawer') {
        isDrawerActive.value = true
        toggleEscListener(menu)
        // Activating nav
        toggleScrolling()
      }
    } else {
      if (menu === 'nav') {
      } else if (menu === 'drawer') {
        isDrawerActive.value = false
        toggleScrolling()
        basket.closeBasket()
      }
    }
  }
}
function navigate(menu) {
  toggleMenu(menu)
}
</script>
